import { writable } from 'svelte/store';

/**
 * A confirmation the user has been shown and has not answered yet.
 *
 * Every field is resolved, so the dialog renders it without applying defaults
 * of its own.
 */
export interface PendingConfirm {
    title: string;
    /** Body text. Newlines are preserved when rendered. */
    message: string;
    confirmLabel: string;
    cancelLabel: string;
    /** True when the action destroys data, so the confirm button reads as a warning. */
    danger: boolean;
}

/** What a caller supplies. Everything but the message has a default. */
export interface ConfirmOptions {
    title?: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    danger?: boolean;
}

const pending = writable<PendingConfirm | null>(null);

/** The confirmation on screen, or null when there is none. Read by ConfirmDialog. */
export const pendingConfirm = { subscribe: pending.subscribe };

let settle: ((confirmed: boolean) => void) | null = null;

/**
 * Asks the user to confirm an action and resolves to their answer.
 *
 * This replaces `window.confirm()`, which a browser is free to suppress — it
 * then returns false, and the guarded action silently does nothing with no
 * request sent and no error shown. Chrome suppresses it once a user ticks
 * "Prevent this page from creating additional dialogs", and embedded browsers
 * disable it outright.
 */
export function confirmAction(options: ConfirmOptions): Promise<boolean> {
    // A confirmation still on screen is answered as declined, so its caller is
    // never left awaiting a promise that can no longer resolve.
    settle?.(false);

    return new Promise<boolean>((resolve) => {
        settle = (confirmed: boolean) => {
            settle = null;
            pending.set(null);
            resolve(confirmed);
        };

        pending.set({
            title: options.title ?? 'Are you sure?',
            message: options.message,
            confirmLabel: options.confirmLabel ?? 'Confirm',
            cancelLabel: options.cancelLabel ?? 'Cancel',
            danger: options.danger ?? false,
        });
    });
}

/**
 * Answers the confirmation currently on screen. Called by ConfirmDialog; a
 * no-op when nothing is pending.
 */
export function answerConfirm(confirmed: boolean): void {
    settle?.(confirmed);
}
